import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

interface RenameProjectModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  projectId: string;
  currentName: string;
  onRename: (id: string, newName: string) => void;
}

export function RenameProjectModal({
  open,
  onOpenChange,
  projectId,
  currentName,
  onRename,
}: RenameProjectModalProps) {
  const [projectName, setProjectName] = useState(currentName);

  // Update the input when the modal opens with a new project
  useEffect(() => {
    if (open) {
      setProjectName(currentName);
    }
  }, [open, currentName]);

  const handleSave = () => {
    if (projectName.trim() && projectName.trim() !== currentName) {
      onRename(projectId, projectName.trim());
      onOpenChange(false);
    }
  };

  const handleClose = () => {
    setProjectName(currentName);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md bg-background border-border">
        <DialogHeader>
          <DialogTitle className="text-foreground">Rename Project</DialogTitle>
          <DialogDescription className="text-muted-foreground">
            Enter a new name for your project.
          </DialogDescription>
        </DialogHeader>
        <div className="py-4">
          <Input
            value={projectName}
            onChange={(e) => setProjectName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSave()}
            className="bg-background border-border focus:border-primary"
            autoFocus
            onFocus={(e) => e.target.select()}
          />
        </div>
        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={!projectName.trim() || projectName.trim() === currentName}
            className="bg-primary text-primary-foreground hover:bg-accent hover:text-accent-foreground"
          >
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
